import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../components/ui/use-toast';
import {
  UserGroupIcon,
  PlusIcon,
  UserIcon,
  BuildingOfficeIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { X } from 'lucide-react';

// Rol çevirisi
const ROLE_TR = {
  super_admin: 'Süper Admin',
  admin: 'Admin',
  supervisor: 'Süpervizör',
  team_leader: 'Ekip Lideri',
  operator: 'Operatör',
  paramedic: 'Paramedik',
  external_institution: 'Dış Kurum',
  mobile_user: 'Mobil Kullanıcı',
};

const roleTr = (role) => ROLE_TR[role] || role || '';

const TeamsPage = () => {
  const { user } = useAuthStore();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showNewTeamModal, setShowNewTeamModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [search, setSearch] = useState('');

  const canManageTeams = ['admin', 'super_admin', 'supervisor'].includes(user?.role);

  // Ekipleri çek
  const { data: teamsResponse, isLoading, error } = useQuery({
    queryKey: ['teams'],
    queryFn: () => api.get('/teams').then((res) => res.data),
  });

  // Lider adaylarını çek (sadece paramedikler)
  const { data: usersResponse } = useQuery({
    queryKey: ['users', 'leaders'],
    queryFn: () =>
      api
        .get('/users', { params: { role: 'paramedic', limit: 500 } })
        .then((res) => res.data),
    enabled: canManageTeams,
  });

  // Doğal sıralama: önce harf grubuna (M, T vb.), sonra numaraya göre
  const naturalSort = (a, b) => {
    const codeA = a.code || '';
    const codeB = b.code || '';
    const prefixA = (codeA.match(/^[A-Za-zÇçĞğİıÖöŞşÜü]+/) || [''])[0].toUpperCase();
    const prefixB = (codeB.match(/^[A-Za-zÇçĞğİıÖöŞşÜü]+/) || [''])[0].toUpperCase();
    if (prefixA !== prefixB) return prefixA.localeCompare(prefixB, 'tr');
    const numA = parseInt((codeA.match(/\d+/) || ['0'])[0], 10);
    const numB = parseInt((codeB.match(/\d+/) || ['0'])[0], 10);
    return numA - numB;
  };

  const teamsData = [...(teamsResponse?.data || [])].sort(naturalSort);
  const filteredTeams = teamsData.filter((team) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (team.name || '').toLowerCase().includes(term) ||
      (team.code || '').toLowerCase().includes(term) ||
      (team.gps_device_no || '').toLowerCase().includes(term)
    );
  });
  const missingGpsCount = teamsData.filter((t) => !t.gps_device_no).length;
  const leaderOptions =
    (usersResponse?.data?.users || [])
      .map((u) => ({
        id: u.id,
        name: `${u.first_name} ${u.last_name}`,
        role: u.role,
      }));

  const handleEditTeam = (team) => {
    setSelectedTeam(team);
    setShowEditTeamModal(true);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Başlık */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Ekipler
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Ambulans ekiplerini görüntüleyin ve yönetin
          </p>
        </div>
        {canManageTeams && (
          <Button
            onClick={() => setShowNewTeamModal(true)}
            className="bg-blue-600 hover:bg-blue-700 shadow-sm w-full sm:w-auto"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Yeni Ekip
          </Button>
        )}
      </div>

      {/* İçerik */}
      <Card className="border-0 shadow-sm bg-slate-50/50">
        <CardHeader className="pb-4 space-y-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <CardTitle className="text-lg text-slate-800">Ekip Listesi</CardTitle>
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="flex-1 md:flex-none md:w-64">
                <Input
                  placeholder="Ekip adı / kodu / GPS no ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 text-sm"
                />
              </div>
            </div>
          </div>
          {missingGpsCount > 0 && (
            <p className="text-[11px] text-amber-600">
              {missingGpsCount} ekip için GPS cihaz numarası tanımlı değil. Arvento konum takibi için bu ekiplerin
              kartındaki <span className="font-semibold">GPS Cihaz No</span> alanını doldurun.
            </p>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Ekipler yükleniyor...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-600 mb-3 text-sm">Hata: {error.message}</p>
              <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
                Yeniden Dene
              </Button>
            </div>
          ) : filteredTeams.length === 0 ? (
            <div className="text-center py-16">
              <UserGroupIcon className="h-14 w-14 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-2">
                Arama kriterlerinize uyan ekip bulunamadı.
              </p>
              {teamsData.length === 0 && (
                <p className="text-xs text-slate-400 mb-4">
                  Henüz sistemde kayıtlı ekip yok.
                </p>
              )}
              {canManageTeams && (
                <Button
                  variant="outline"
                  onClick={() => setShowNewTeamModal(true)}
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Yeni Ekip Ekle
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {filteredTeams.map((team) => (
                <div
                  key={team.id}
                  className="group relative rounded-xl border border-slate-200 bg-white p-5 transition-all duration-200 hover:shadow-lg hover:border-blue-200 hover:-translate-y-0.5"
                >
                  {/* Üst: İsim + Kod */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm">
                        <UserGroupIcon className="h-5 w-5 text-white" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-slate-900 text-sm leading-tight truncate">
                          {team.name}
                        </h3>
                      </div>
                    </div>
                    <span className="shrink-0 ml-2 inline-flex items-center rounded-md bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-600/20">
                      {team.code}
                    </span>
                  </div>

                  {/* Orta: Lider bilgisi */}
                  <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2.5 mb-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white border border-slate-200 shadow-sm">
                      <UserIcon className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-medium uppercase tracking-wider text-slate-400 leading-none mb-1">
                        Ekip Lideri
                      </p>
                      {team.leader_name ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-medium text-slate-800 truncate">
                            {team.leader_name}
                          </span>
                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
                            {roleTr(team.leader_role)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400 italic">
                          Atanmamış
                        </span>
                      )}
                    </div>
                  </div>

                  {/* GPS cihaz numarası */}
                  <div className="mb-3 flex items-center justify-between text-xs">
                    <span className="font-medium text-slate-600">GPS Cihaz No</span>
                    <span
                      className={`font-mono text-[11px] ${
                        team.gps_device_no ? 'text-slate-700' : 'text-amber-600 italic'
                      }`}
                    >
                      {team.gps_device_no || 'Tanımlı değil'}
                    </span>
                  </div>

                  {/* Alt: Kurum + Düzenle butonu */}
                  <div className="flex items-center justify-between">
                    {team.institution_name ? (
                      <div className="flex items-center gap-1.5 text-xs text-slate-500 min-w-0">
                        <BuildingOfficeIcon className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                        <span className="truncate">{team.institution_name}</span>
                      </div>
                    ) : (
                      <div />
                    )}
                    {canManageTeams && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-2.5 text-xs text-slate-500 hover:text-blue-600 hover:bg-blue-50 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleEditTeam(team)}
                      >
                        <PencilSquareIcon className="h-3.5 w-3.5 mr-1" />
                        Düzenle
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Yeni Ekip Modal */}
      {showNewTeamModal && (
        <NewTeamModal
          leaders={leaderOptions}
          onClose={() => setShowNewTeamModal(false)}
          onSuccess={() => {
            setShowNewTeamModal(false);
            queryClient.invalidateQueries(['teams']);
          }}
        />
      )}

      {/* Ekip Düzenle Modal */}
      {showEditTeamModal && selectedTeam && (
        <EditTeamModal
          team={selectedTeam}
          leaders={leaderOptions}
          onClose={() => {
            setShowEditTeamModal(false);
            setSelectedTeam(null);
          }}
          onSuccess={() => {
            setShowEditTeamModal(false);
            setSelectedTeam(null);
            queryClient.invalidateQueries(['teams']);
          }}
        />
      )}
    </div>
  );
};

/* ──────────────────────── Yeni Ekip Modal ──────────────────────── */
const NewTeamModal = ({ leaders, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    leader_user_id: '',
    gps_device_no: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast({ title: 'Hata', description: 'Ekip adı ve kodu zorunludur', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await api.post('/teams', {
        name: formData.name,
        code: formData.code,
        leader_user_id: formData.leader_user_id || null,
        gps_device_no: formData.gps_device_no?.trim() || null,
      });
      toast({ title: 'Başarılı', description: 'Ekip başarıyla oluşturuldu', variant: 'success' });
      onSuccess();
    } catch (error) {
      console.error('Server error:', error.response?.data);
      toast({
        title: 'Hata',
        description: error.response?.data?.message || error.response?.data?.details || 'Ekip oluşturulurken hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-slate-900">Yeni Ekip Oluştur</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-sm font-medium text-slate-700">
              Ekip Adı
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Örn: M-1 Ambulans Ekibi"
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="code" className="text-sm font-medium text-slate-700">
              Ekip Kodu
            </Label>
            <Input
              id="code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="Örn: M-1"
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="leader" className="text-sm font-medium text-slate-700">
              Ekip Lideri
            </Label>
            <select
              id="leader"
              className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
              value={formData.leader_user_id}
              onChange={(e) =>
                setFormData({ ...formData, leader_user_id: e.target.value })
              }
            >
              <option value="">Lider seçin (opsiyonel)</option>
              {leaders.map((leader) => (
                <option key={leader.id} value={leader.id}>
                  {leader.name} — {roleTr(leader.role)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="gps_device_no" className="text-sm font-medium text-slate-700">
              GPS Cihaz No (Arvento)
            </Label>
            <Input
              id="gps_device_no"
              value={formData.gps_device_no}
              onChange={(e) =>
                setFormData({ ...formData, gps_device_no: e.target.value })
              }
              placeholder="Örn: 3202435"
              className="h-10"
            />
            <p className="text-[11px] text-slate-400">
              Arvento sisteminde görünen Cihaz No ile birebir aynı olmalıdır.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose} className="h-10">
              İptal
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="h-10 bg-blue-600 hover:bg-blue-700 shadow-sm"
            >
              {loading ? 'Oluşturuluyor...' : 'Oluştur'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ──────────────────────── Ekip Düzenle Modal ──────────────────────── */
const EditTeamModal = ({ team, leaders, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: team.name || '',
    code: team.code || '',
    leader_user_id: team.leader_user_id || '',
    gps_device_no: team.gps_device_no || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toast({ title: 'Hata', description: 'Ekip adı ve kodu zorunludur', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      await api.put(`/teams/${team.id}`, {
        name: formData.name,
        code: formData.code,
        leader_user_id: formData.leader_user_id || null,
        gps_device_no: formData.gps_device_no?.trim() || null,
      });
      toast({ title: 'Başarılı', description: 'Ekip başarıyla güncellendi', variant: 'success' });
      onSuccess();
    } catch (error) {
      toast({
        title: 'Hata',
        description: error.response?.data?.message || 'Ekip güncellenirken hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-slate-900">Ekip Düzenle</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-name" className="text-sm font-medium text-slate-700">
              Ekip Adı
            </Label>
            <Input
              id="edit-name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Örn: M-1 Ambulans Ekibi"
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-code" className="text-sm font-medium text-slate-700">
              Ekip Kodu
            </Label>
            <Input
              id="edit-code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="Örn: M-1"
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-leader" className="text-sm font-medium text-slate-700">
              Ekip Lideri
            </Label>
            <select
              id="edit-leader"
              className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-colors"
              value={formData.leader_user_id}
              onChange={(e) =>
                setFormData({ ...formData, leader_user_id: e.target.value })
              }
            >
              <option value="">Lider seçin (opsiyonel)</option>
              {leaders.map((leader) => (
                <option key={leader.id} value={leader.id}>
                  {leader.name} — {roleTr(leader.role)}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-gps_device_no" className="text-sm font-medium text-slate-700">
              GPS Cihaz No (Arvento)
            </Label>
            <Input
              id="edit-gps_device_no"
              value={formData.gps_device_no}
              onChange={(e) =>
                setFormData({ ...formData, gps_device_no: e.target.value })
              }
              placeholder="Örn: 3202435"
              className="h-10"
            />
            <p className="text-[11px] text-slate-400">
              Arvento sistemindeki Cihaz No ile aynı olmalıdır. Boş bırakırsanız bu ekip için araç konumu izlenemez.
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
            <Button type="button" variant="outline" onClick={onClose} className="h-10">
              İptal
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="h-10 bg-blue-600 hover:bg-blue-700 shadow-sm"
            >
              {loading ? 'Güncelleniyor...' : 'Güncelle'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamsPage;
